import pLimit from 'p-limit'

import { ALL_TIME_RANGE_QUERY } from 'consts/filterOptions'
import type {
  GetTerritoryChildrenQuery,
  GetTerritoryChildrenQueryVariables,
  ListTerritoriesTotalsQuery,
  ListTerritoriesTotalsQueryVariables,
} from 'generated/graphql'
import { Providers } from 'generated/graphql'
import { GET_TERRITORY_CHILDREN, LIST_TERRITORIES_TOTALS } from 'graphql/territory/operations'
import getUploadDateByValue from 'utils/getUploadDateByValue'
import { getFormatedDateVariablesForTerritory } from 'utils/territoryUtils'

import {
  GET_TERRITORY_CHILDREN_LOG,
  LIST_TERRITORIES_TOTALS_LOG,
  TAXONOMY_CHILDREN_PAGE_SIZE,
  TAXONOMY_CHILDREN_SORT,
  TAXONOMY_CHILDREN_TOTALS_CONCURRENCY,
} from './taxonomy-children-tab.consts'
import type {
  ILoadTaxonomyChildrenParams,
  ITaxonomyChild,
  TChartSelectionToggle,
  TTaxonomyChildrenClient,
  TTaxonomyChildrenLoadAction,
  TTaxonomyChildrenLoadState,
  TTaxonomyChildrenTotalsById,
  TTaxonomyChildrenTotalsVariables,
  TTaxonomyChildTotals,
} from './taxonomy-children-tab.types'

const createTaxonomyChildrenLoadState = (key: string): TTaxonomyChildrenLoadState => ({
  key,
  children: [],
  totalsById: {},
  failedIds: [],
  isComplete: false,
  hasError: false,
})

const fetchTaxonomyChildrenPage = async (
  client: TTaxonomyChildrenClient,
  territoryId: string,
  cursor: string | null
): Promise<{ children: ITaxonomyChild[]; cursor: string | null }> => {
  const { data } = await client.query<GetTerritoryChildrenQuery, GetTerritoryChildrenQueryVariables>({
    query: GET_TERRITORY_CHILDREN,
    variables: { territoryId, take: TAXONOMY_CHILDREN_PAGE_SIZE, cursor, ...TAXONOMY_CHILDREN_SORT },
    context: { errorLog: GET_TERRITORY_CHILDREN_LOG },
  })
  const territory = data?.getTerritory
  if (!territory || !('children' in territory)) return { children: [], cursor: null }

  const children = territory.children.data.map(({ territoryId, name }) => ({ territoryId, name }))
  return { children, cursor: territory.children.cursor ?? null }
}

const fetchTaxonomyChildTotals = async (
  client: TTaxonomyChildrenClient,
  territoryId: string,
  variables: TTaxonomyChildrenTotalsVariables
): Promise<TTaxonomyChildTotals> => {
  const { data } = await client.query<ListTerritoriesTotalsQuery, ListTerritoriesTotalsQueryVariables>({
    query: LIST_TERRITORIES_TOTALS,
    variables: { ...variables, ids: [territoryId] },
    context: { errorLog: LIST_TERRITORIES_TOTALS_LOG },
  })
  const metrics = data?.listTerritories?.[0]?.territoryPerformance?.metrics
  return {
    totalEngagement: Math.max(0, metrics?.totalEngagement ?? 0),
    totalViews: Math.max(0, metrics?.totalViews ?? 0),
    totalPosts: Math.max(0, metrics?.totalPosts ?? 0),
  }
}

const withOneRetry = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request()
  } catch {
    return request()
  }
}

async function* fetchTaxonomyChildrenPages(
  client: TTaxonomyChildrenClient,
  territoryId: string
): AsyncGenerator<ITaxonomyChild[]> {
  let cursor: string | null = null
  do {
    const pageCursor = cursor
    const page = await withOneRetry(() => fetchTaxonomyChildrenPage(client, territoryId, pageCursor))
    yield page.children
    cursor = page.cursor
  } while (cursor)
}

export const loadTaxonomyChildrenProgressively = async ({
  client,
  territoryId,
  variables,
  onChildrenPage,
  onChildSettled,
  isCancelled,
}: ILoadTaxonomyChildrenParams): Promise<void> => {
  const limitTotals = pLimit(TAXONOMY_CHILDREN_TOTALS_CONCURRENCY)
  const totalsRequests: Promise<void>[] = []
  let hasLoadedAPage = false

  const settleChildTotals = async (childId: string) => {
    if (isCancelled()) return
    const totals = await withOneRetry(() => fetchTaxonomyChildTotals(client, childId, variables)).catch(() => null)
    if (!isCancelled()) onChildSettled(childId, totals)
  }

  try {
    for await (const children of fetchTaxonomyChildrenPages(client, territoryId)) {
      if (isCancelled()) return
      hasLoadedAPage = true
      if (!children.length) continue

      onChildrenPage(children)
      totalsRequests.push(...children.map(child => limitTotals(() => settleChildTotals(child.territoryId))))
    }
  } catch (error) {
    if (!hasLoadedAPage) throw error
  }

  await Promise.all(totalsRequests)
}

export const taxonomyChildrenLoadReducer = (
  state: TTaxonomyChildrenLoadState | undefined,
  action: TTaxonomyChildrenLoadAction
): TTaxonomyChildrenLoadState => {
  const current = state?.key === action.key ? state : createTaxonomyChildrenLoadState(action.key)

  switch (action.type) {
    case 'page-loaded':
      return { ...current, children: [...current.children, ...action.children] }
    case 'child-settled':
      return action.totals
        ? { ...current, totalsById: { ...current.totalsById, [action.childId]: action.totals } }
        : { ...current, failedIds: [...current.failedIds, action.childId] }
    case 'completed':
      return { ...current, isComplete: true }
    case 'failed':
      return { ...current, isComplete: true, hasError: true }
  }
}

const isProvider = (value: string): value is Providers => Object.values<string>(Providers).includes(value)

export const getTaxonomyChildrenTotalsVariables = (
  uploadDateParam: string | null,
  providersKey: string
): TTaxonomyChildrenTotalsVariables => {
  const providers = providersKey.split(',').filter(isProvider)
  const isCustomDate = Boolean(uploadDateParam?.includes('custom'))
  const isAllTime = uploadDateParam === 'all'

  if (isCustomDate || isAllTime) {
    return {
      providers,
      timeRange: null,
      publishedAt: isCustomDate ? getUploadDateByValue(uploadDateParam) : ALL_TIME_RANGE_QUERY,
    }
  }

  const dateValue = getFormatedDateVariablesForTerritory(uploadDateParam)
  return {
    providers,
    timeRange: { timeUnit: dateValue?.timeUnit, timeValue: dateValue?.timeValue },
  }
}

export const sortTaxonomyChildrenByEngagement = (
  children: ITaxonomyChild[],
  totalsById: TTaxonomyChildrenTotalsById
): ITaxonomyChild[] => {
  const getEngagement = (child: ITaxonomyChild) => totalsById[child.territoryId]?.totalEngagement ?? -1
  return [...children].sort((a, b) => getEngagement(b) - getEngagement(a))
}

export const toggleChartSelection = ({
  id,
  selectedIds,
  defaultIds,
  limit,
}: {
  id: string
  selectedIds: string[]
  defaultIds: string[]
  limit: number
}): TChartSelectionToggle => {
  const visibleIds = selectedIds.length ? selectedIds : defaultIds

  if (visibleIds.includes(id)) {
    if (visibleIds.length === 1) return { status: 'min-reached' }
    return { status: 'updated', selectedIds: visibleIds.filter(visibleId => visibleId !== id) }
  }

  if (!selectedIds.length || selectedIds.length >= limit) return { status: 'limit-reached' }

  return { status: 'updated', selectedIds: [...selectedIds, id] }
}
